// 문해력 검사 문항 데이터
// 일상 / 시사 / 경제 / 과학기술 / 문화예술 / 인문 6개 유형 × 4문항 = 24문항
// 각 문항은 지문을 읽고 내용을 파악해 답하는 형태이다.

export type ReadingType =
  | 'daily'
  | 'current'
  | 'economy'
  | 'science'
  | 'culture'
  | 'humanities';

export interface ReadingQuestion {
  id: string;
  type: ReadingType;
  passage: string;
  question: string;
  options: string[];
  answer: number;
  explanation: string;
}

export interface ReadingTypeMeta {
  id: ReadingType;
  label: string;
  icon: string;
  description: string;
}

export const readingTypeMeta: ReadingTypeMeta[] = [
  { id: 'daily', label: '일상 지문', icon: 'ri-home-line', description: '생활 속 경험과 습관을 다룬 지문' },
  { id: 'current', label: '시사 지문', icon: 'ri-newspaper-line', description: '사회 문제와 현안을 다룬 지문' },
  { id: 'economy', label: '경제 지문', icon: 'ri-line-chart-line', description: '경제·금융 현상을 다룬 지문' },
  { id: 'science', label: '과학기술 지문', icon: 'ri-flask-line', description: '과학 원리와 기술을 다룬 지문' },
  { id: 'culture', label: '문화예술 지문', icon: 'ri-palette-line', description: '전통·예술·문화를 다룬 지문' },
  { id: 'humanities', label: '인문 지문', icon: 'ri-book-open-line', description: '삶과 가치에 대한 사유를 다룬 지문' },
];

export const readingQuestions: ReadingQuestion[] = [
  // ===== 일상 지문 (daily) =====
  {
    id: 'r01',
    type: 'daily',
    passage: '아침에 일어나면 창문을 열고 잠시 바깥 공기를 마신다. 그동안 차곡차곡 쌓였던 잠의 무게가 가벼워지는 느낌이 든다. 이 짧은 시간 덕분에 하루가 훨씬 상쾌하게 시작된다.',
    question: '이 글의 중심 내용으로 가장 알맞은 것은?',
    options: ['아침에 창문을 열고 바깥 공기를 마시는 습관이 하루를 상쾌하게 만든다', '잠을 많이 자야 하루가 상쾌하다', '창문을 자주 닫아 두어야 건강하다', '아침에는 되도록 늦게 일어나는 것이 좋다'],
    answer: 0,
    explanation: '글쓴이는 아침에 창문을 열고 바깥 공기를 마시는 습관이 하루를 상쾌하게 시작하게 한다고 말하고 있다.',
  },
  {
    id: 'r02',
    type: 'daily',
    passage: '요즘은 혼자 밥을 먹는 사람이 많아졌다. 식당에는 1인용 좌석이 따로 마련되어 있고, 배달 음식을 시켜 집에서 먹는 사람도 늘었다. 혼자 먹는 것이 더 편하다는 사람이 적지 않다.',
    question: '윗글의 내용과 일치하는 것은?',
    options: ['혼자 밥을 먹는 사람이 줄어들고 있다', '식당에 1인용 좌석이 따로 마련되어 있다', '배달 음식을 시켜 먹는 사람이 줄었다', '모든 사람이 여럿이 먹는 것을 더 좋아한다'],
    answer: 1,
    explanation: '지문에 식당에 1인용 좌석이 따로 마련되어 있다고 명시되어 있다.',
  },
  {
    id: 'r03',
    type: 'daily',
    passage: '반려동물을 키우는 사람이 늘면서 이웃 간의 갈등도 함께 늘고 있다. 산책할 때 목줄을 하지 않거나 소음을 신경 쓰지 않는 경우가 대표적이다. 함께 살아가려면 서로를 배려하는 자세가 필요하다.',
    question: '윗글에서 말하는 갈등의 원인으로 가장 알맞은 것은?',
    options: ['반려동물의 수가 너무 적어서', '산책할 때 목줄을 하지 않거나 소음을 신경 쓰지 않는 경우', '동물 병원이 너무 많아서', '반려동물 사료 가격이 올라서'],
    answer: 1,
    explanation: '지문은 산책할 때 목줄을 하지 않거나 소음을 신경 쓰지 않는 경우를 갈등의 대표적 원인으로 제시한다.',
  },
  {
    id: 'r04',
    type: 'daily',
    passage: '꾸준한 운동은 몸에 좋다는 것을 알면서도 실천하기는 쉽지 않다. 처음부터 무리하게 시작하면 오래 가지 못한다. 가벼운 걷기부터 천천히 습관을 들이는 것이 효과적이다.',
    question: '운동을 오래 지속하기 위한 방법으로 윗글이 제시하는 것은?',
    options: ['처음부터 힘든 운동을 많이 한다', '남과 경쟁하며 운동한다', '가벼운 걷기부터 천천히 습관을 들인다', '운동을 자주 쉬어 준다'],
    answer: 2,
    explanation: '지문은 무리하게 시작하지 말고 가벼운 걷기부터 천천히 습관을 들이는 것이 효과적이라고 한다.',
  },

  // ===== 시사 지문 (current) =====
  {
    id: 'r05',
    type: 'current',
    passage: '쓰레기를 줄이기 위해 일회용품 사용을 줄이자는 목소리가 커지고 있다. 플라스틱은 자연에서 분해되는 데 수백 년이 걸린다. 정부는 일회용품 규제를 강화하고, 기업들도 친환경 포장재를 도입하고 있다.',
    question: '일회용품 사용을 줄이려는 이유로 가장 알맞은 것은?',
    options: ['플라스틱이 자연에서 분해되는 데 수백 년이 걸리기 때문', '플라스틱이 너무 비싸기 때문', '일회용품을 만드는 기술이 없기 때문', '플라스틱이 너무 무겁기 때문'],
    answer: 0,
    explanation: '지문은 플라스틱이 자연에서 분해되는 데 수백 년이 걸린다는 점을 일회용품 사용을 줄이려는 이유로 든다.',
  },
  {
    id: 'r06',
    type: 'current',
    passage: '인공지능 기술이 빠르게 발전하면서 우리 생활이 편리해졌다. 하지만 인공지능이 사람의 일자리를 대체할 것이라는 우려도 커지고 있다. 전문가들은 인공지능과 함께 일할 수 있는 능력을 기르는 것이 중요하다고 말한다.',
    question: '전문가들이 강조하는 것은 무엇인가?',
    options: ['인공지능을 완전히 없애야 한다', '인공지능과 함께 일할 수 있는 능력을 길러야 한다', '인공지능 사용을 법으로 금지해야 한다', '일자리를 모두 없애야 한다'],
    answer: 1,
    explanation: '지문은 전문가들이 인공지능과 함께 일할 수 있는 능력을 기르는 것이 중요하다고 말한다고 밝힌다.',
  },
  {
    id: 'r07',
    type: 'current',
    passage: '우리나라의 출생률이 계속 낮아지고 있다. 아이를 낳고 기르는 데 드는 비용 부담이 큰 것이 주요 원인으로 꼽힌다. 이에 정부는 육아 지원을 확대하는 등 다양한 대책을 내놓고 있다.',
    question: '출생률이 낮아지는 주요 원인으로 윗글이 꼽는 것은?',
    options: ['교육의 질이 너무 높아서', '아이를 낳고 기르는 데 드는 비용 부담', '의료 기술이 부족해서', '도시 인구가 너무 적어서'],
    answer: 1,
    explanation: '지문은 아이를 낳고 기르는 데 드는 비용 부담이 큰 것을 출생률 저하의 주요 원인으로 꼽는다.',
  },
  {
    id: 'r08',
    type: 'current',
    passage: '지구의 평균 기온이 계속 오르면서 이상 기후 현상이 잦아지고 있다. 폭염과 홍수, 가뭄이 곳곳에서 발생하고 있다. 온실가스 배출을 줄이기 위한 국제적인 노력이 이어지고 있다.',
    question: '이상 기후 현상의 예로 윗글에서 언급되지 않은 것은?',
    options: ['폭염', '홍수', '가뭄', '지진'],
    answer: 3,
    explanation: '지문은 폭염과 홍수, 가뭄을 이상 기후의 예로 들었고, 지진은 언급하지 않았다.',
  },

  // ===== 경제 지문 (economy) =====
  {
    id: 'r09',
    type: 'economy',
    passage: '금리가 오르면 예금하는 사람에게는 이자가 늘어나 이익이 된다. 하지만 돈을 빌린 사람은 갚아야 할 이자가 많아져 부담이 커진다. 금리 변동은 이처럼 사람마다 미치는 영향이 다르다.',
    question: '금리가 오를 때 부담이 커지는 사람은 누구인가?',
    options: ['예금하는 사람', '돈을 빌린 사람', '금융회사 직원', '금리와 관계없는 사람'],
    answer: 1,
    explanation: '지문은 금리가 오르면 돈을 빌린 사람은 갚아야 할 이자가 많아져 부담이 커진다고 설명한다.',
  },
  {
    id: 'r10',
    type: 'economy',
    passage: '최저임금은 근로자가 받아야 하는 최소한의 임금이다. 최저임금이 오르면 저임금 근로자의 생활이 나아질 수 있다. 반면 인건비 부담이 커진 자영업자들은 어려움을 겪기도 한다.',
    question: '최저임금 인상이 자영업자에게 미치는 영향으로 가장 알맞은 것은?',
    options: ['수익이 반드시 크게 늘어난다', '인건비 부담이 커져 어려움을 겪을 수 있다', '아무런 영향이 없다', '근로자를 더 많이 고용하게 된다'],
    answer: 1,
    explanation: '지문은 최저임금이 오르면 인건비 부담이 커진 자영업자들이 어려움을 겪기도 한다고 말한다.',
  },
  {
    id: 'r11',
    type: 'economy',
    passage: '최근 소비자들은 물건을 오래 쓰기보다 필요할 때만 빌려 쓰는 경향이 커지고 있다. 자동차를 빌려 타거나 옷을 대여하는 사람이 늘어난 것이다. 이런 소비를 공유 소비라고 부른다.',
    question: '윗글에서 설명하는 공유 소비의 특징으로 가장 알맞은 것은?',
    options: ['물건을 사서 영원히 소유한다', '필요할 때만 빌려 쓴다', '물건을 절대 사용하지 않는다', '중고 물건만 사서 쓴다'],
    answer: 1,
    explanation: '공유 소비는 물건을 소유하기보다 필요할 때만 빌려 쓰는 소비 방식이다.',
  },
  {
    id: 'r12',
    type: 'economy',
    passage: '한 나라의 수출이 늘면 외국에서 돈이 들어와 경제에 도움이 된다. 수입이 수출보다 많으면 무역수지 적자가 발생한다. 수출과 수입의 균형은 경제에 중요한 영향을 미친다.',
    question: '무역수지 적자가 발생하는 경우는 언제인가?',
    options: ['수출이 수입보다 많을 때', '수출과 수입이 같을 때', '수입이 수출보다 많을 때', '수출을 아예 하지 않을 때'],
    answer: 2,
    explanation: '지문은 수입이 수출보다 많으면 무역수지 적자가 발생한다고 설명한다.',
  },

  // ===== 과학기술 지문 (science) =====
  {
    id: 'r13',
    type: 'science',
    passage: '백신은 병을 일으키는 세균이나 바이러스를 약하게 하여 몸에 넣는 것이다. 그러면 우리 몸은 그 병원균을 기억하고, 나중에 진짜 병원균이 들어와도 빠르게 대응할 수 있게 된다. 이것이 면역의 원리이다.',
    question: '백신을 맞으면 몸에 생기는 능력은 무엇인가?',
    options: ['병원균을 기억하고 빠르게 대응하는 능력', '음식을 빨리 소화하는 능력', '키가 빨리 크는 능력', '잠을 더 잘 자는 능력'],
    answer: 0,
    explanation: '백신은 몸이 병원균을 기억하여 나중에 빠르게 대응할 수 있게 하는 면역의 원리를 이용한다.',
  },
  {
    id: 'r14',
    type: 'science',
    passage: '별빛은 별에서 지구까지 오는 데 오랜 시간이 걸린다. 그래서 우리가 밤하늘에서 보는 별은 지금의 모습이 아니라 오래전의 모습이다. 아주 먼 별일수록 더 오래전의 모습을 보게 되는 셈이다.',
    question: '윗글의 내용으로 볼 때, 가장 오래전 모습을 보게 되는 별은?',
    options: ['가장 가까운 별', '가장 밝은 별', '가장 먼 별', '가장 큰 별'],
    answer: 2,
    explanation: '지문은 아주 먼 별일수록 더 오래전의 모습을 보게 된다고 설명한다.',
  },
  {
    id: 'r15',
    type: 'science',
    passage: '생물의 모든 형질은 유전자에 기록되어 있다. 유전자는 부모에게서 자식에게 전달된다. 눈동자 색이나 머리카락 색 같은 특징도 유전자의 영향을 받는다.',
    question: '유전자가 전달되는 경로로 가장 알맞은 것은?',
    options: ['친구에게서 친구에게', '부모에게서 자식에게', '선생님에게서 학생에게', '형제에게서 형제에게'],
    answer: 1,
    explanation: '지문은 유전자가 부모에게서 자식에게 전달된다고 설명한다.',
  },
  {
    id: 'r16',
    type: 'science',
    passage: '태양이나 바람 같은 자연에서 얻는 에너지는 다시 만들어 쓸 수 있어 재생 에너지라고 부른다. 반면 석유나 석탄은 한 번 쓰면 다시 만들기 어렵다. 재생 에너지는 환경 오염을 줄이는 데도 도움이 된다.',
    question: '재생 에너지에 해당하지 않는 것은?',
    options: ['태양 에너지', '바람 에너지', '석유', '수력 에너지'],
    answer: 2,
    explanation: '석유는 한 번 쓰면 다시 만들기 어려운 화석 연료로 재생 에너지에 해당하지 않는다.',
  },

  // ===== 문화예술 지문 (culture) =====
  {
    id: 'r17',
    type: 'culture',
    passage: '판소리는 한 명의 소리꾼이 북 장단에 맞추어 긴 이야기를 노래하는 우리나라의 전통 음악이다. 판소리는 소리꾼과 청중이 서로 호흡하며 완성되는 예술이다. 청중의 추임새는 공연에 활기를 더한다.',
    question: '판소리에 대한 설명으로 옳지 않은 것은?',
    options: ['한 명의 소리꾼이 노래한다', '북 장단에 맞춘다', '긴 이야기를 노래한다', '청중 없이 소리꾼 혼자만으로 완성된다'],
    answer: 3,
    explanation: '판소리는 소리꾼과 청중이 서로 호흡하며 완성되는 예술이므로, 청중 없이 혼자만으로 완성된다는 설명은 옳지 않다.',
  },
  {
    id: 'r18',
    type: 'culture',
    passage: '미술관에서 작품을 감상할 때는 작품과 거리를 두고 천천히 둘러보는 것이 좋다. 작품의 제목과 설명을 읽으면 작가의 의도를 이해하는 데 도움이 된다. 감상은 정해진 답을 찾는 것이 아니라 나만의 느낌을 찾는 과정이다.',
    question: '윗글에서 말하는 미술 감상의 올바른 태도로 가장 알맞은 것은?',
    options: ['정해진 정답을 빠르게 찾아야 한다', '작품과 거리를 두고 천천히 둘러보며 나만의 느낌을 찾는다', '작품 설명은 읽지 않는 것이 좋다', '작품을 가까이서 만져 보아야 한다'],
    answer: 1,
    explanation: '지문은 작품과 거리를 두고 천천히 둘러보며 나만의 느낌을 찾는 것이 감상이라고 말한다.',
  },
  {
    id: 'r19',
    type: 'culture',
    passage: '영화는 영상과 소리, 이야기가 어우러진 종합 예술이다. 같은 영화라도 보는 사람에 따라 느낌이 다를 수 있다. 영화를 본 뒤 자신의 감상을 친구와 나누면 새로운 관점을 발견하기도 한다.',
    question: '영화를 본 뒤 감상을 나누면 좋은 점으로 윗글이 말하는 것은?',
    options: ['영화 제작 비용이 줄어든다', '새로운 관점을 발견할 수 있다', '영화 시간이 길어진다', '배우가 더 유명해진다'],
    answer: 1,
    explanation: '지문은 영화를 본 뒤 감상을 나누면 새로운 관점을 발견하기도 한다고 말한다.',
  },
  {
    id: 'r20',
    type: 'culture',
    passage: '한글은 세종대왕이 백성을 위해 만든 문자이다. 누구나 쉽게 배워 쓸 수 있도록 만들어졌다. 한글은 소리 나는 대로 적을 수 있어 배우기 쉬운 문자로 평가받는다.',
    question: '한글을 만든 목적으로 가장 알맞은 것은?',
    options: ['백성이 쉽게 배워 쓸 수 있도록 하기 위해', '다른 나라와 교류하기 위해', '상업 거래를 편하게 하기 위해', '군사 명령을 내리기 위해'],
    answer: 0,
    explanation: '한글은 백성이 쉽게 배워 쓸 수 있도록 세종대왕이 만든 문자이다.',
  },

  // ===== 인문 지문 (humanities) =====
  {
    id: 'r21',
    type: 'humanities',
    passage: '행복은 큰 성공에서만 오는 것이 아니다. 소소한 일상 속에서 감사할 줄 아는 마음이 행복을 키운다. 오늘 하루 있었던 좋은 일을 떠올려 보는 것만으로도 마음이 따뜻해진다.',
    question: '윗글이 말하는 행복의 원천으로 가장 알맞은 것은?',
    options: ['큰 성공과 많은 재산', '일상 속에서 감사할 줄 아는 마음', '남보다 뛰어난 능력', '아무 일도 하지 않는 여유'],
    answer: 1,
    explanation: '지문은 소소한 일상 속에서 감사할 줄 아는 마음이 행복을 키운다고 말한다.',
  },
  {
    id: 'r22',
    type: 'humanities',
    passage: '역사를 배우는 것은 단순히 과거의 일을 외우는 것이 아니다. 과거의 성공과 실패를 통해 현재를 이해하고 미래를 준비하는 것이다. 역사를 아는 사람은 같은 실수를 되풀이하지 않으려 노력한다.',
    question: '역사를 배우는 이유로 윗글이 말하는 것은?',
    options: ['과거의 일을 단순히 외우기 위해', '과거의 성공과 실패를 통해 현재를 이해하고 미래를 준비하기 위해', '옛날 이야기를 재미로 듣기 위해', '시험에서 좋은 점수를 받기 위해'],
    answer: 1,
    explanation: '지문은 역사를 배우는 것이 과거의 성공과 실패를 통해 현재를 이해하고 미래를 준비하는 것이라고 말한다.',
  },
  {
    id: 'r23',
    type: 'humanities',
    passage: '책을 읽으면 글쓴이의 생각과 마주할 수 있다. 때로는 나와 다른 생각을 만나 혼란스러울 때도 있다. 하지만 그 과정에서 자신의 생각이 한층 깊어지고 넓어진다.',
    question: '독서의 가치로 윗글이 말하는 것은?',
    options: ['자신의 생각이 깊어지고 넓어진다', '항상 기분이 좋아진다', '다른 사람보다 우월해진다', '시간을 빨리 보낼 수 있다'],
    answer: 0,
    explanation: '지문은 책을 읽으며 다른 생각을 만나는 과정에서 자신의 생각이 깊어지고 넓어진다고 말한다.',
  },
  {
    id: 'r24',
    type: 'humanities',
    passage: '공감은 다른 사람의 입장이 되어 그 마음을 이해하는 것이다. 공감이 이루어지면 서로의 갈등이 줄어들고 관계가 좋아진다. 공감은 말로만 하는 것이 아니라 행동으로 실천할 때 더 큰 힘을 발휘한다.',
    question: '윗글의 내용과 일치하는 것은?',
    options: ['공감은 다른 사람의 마음과 상관없다', '공감이 이루어지면 갈등이 줄어들고 관계가 좋아진다', '공감은 말로만 하는 것이 가장 효과적이다', '공감은 행동으로 실천할 필요가 없다'],
    answer: 1,
    explanation: '지문은 공감이 이루어지면 갈등이 줄어들고 관계가 좋아진다고 명시하고 있다.',
  },
];